import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LineupBuilder } from "../../../components/LineupBuilder";
import { getCurrentUser, type CurrentUser } from "../../../lib/auth";
import { THEME_ROSTERS, useLeague, useLeagueStandings } from "../../../lib/api/leagues";

// /fantasy/:id — league interior. If the caller hasn't yet submitted a
// lineup, render the LineupBuilder. If they have (i.e. they appear in the
// standings), render the standings table.
export function FantasyLeagueRoute() {
	const { id } = useParams<{ id: string }>();
	const league = useLeague(id);
	const standings = useLeagueStandings(id);
	const [user, setUser] = useState<CurrentUser | null>(null);

	useEffect(() => {
		void getCurrentUser().then(setUser);
	}, []);

	if (league.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center text-[var(--color-muted)]">
				Loading league…
			</div>
		);
	}

	if (league.isError || !league.data) {
		return (
			<div className="mx-auto max-w-2xl px-4 py-8 text-center">
				<p className="text-sm text-[var(--color-danger)]">
					Could not load league.{" "}
					{league.error instanceof Error ? league.error.message : ""}
				</p>
				<Link
					to="/fantasy"
					className="mt-4 inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					Back to Fantasy
				</Link>
			</div>
		);
	}

	const l = league.data;
	const roster = THEME_ROSTERS[l.theme] ?? [];
	const currentUserId = user?.id ?? "";
	const hasEntered = !!standings.data?.entries.some((e) => e.userId === currentUserId);

	return (
		<div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
			<header>
				<Link
					to="/fantasy"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Fantasy
				</Link>
				<h1 className="mt-1 text-2xl font-bold">{l.themeName}</h1>
				<p className="text-sm text-[var(--color-muted)]">{l.description}</p>
				<p className="mt-1 text-xs text-[var(--color-muted)]">
					Status: {l.status} · {l.entries} entries · roster {l.rosterSize}{" "}
					symbols
				</p>
			</header>

			<section className="space-y-3">
				<h2 className="text-sm font-semibold">Roster</h2>
				<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3">
					<div className="flex flex-wrap gap-1.5">
						{roster.map((sym) => (
							<span
								key={sym}
								className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-2 py-0.5 font-mono text-xs"
							>
								{sym}
							</span>
						))}
					</div>
				</div>
			</section>

			{!hasEntered ? (
				<section className="space-y-3">
					<h2 className="text-sm font-semibold">Build your lineup</h2>
					<p className="text-xs text-[var(--color-muted)]">
						Pick 10 unique symbols and allocate exactly 100%. Re-submit any
						time before the week closes to replace your prior lineup.
					</p>
					{l.id ? (
						<LineupBuilder
							leagueId={l.id}
							theme={l.themeName}
							roster={roster}
							onSubmitted={() => void standings.refetch()}
						/>
					) : (
						<p className="text-sm text-[var(--color-muted)]">
							This theme isn't open yet — check back once the weekly worker
							seeds it.
						</p>
					)}
				</section>
			) : (
				<section className="space-y-3">
					<h2 className="text-sm font-semibold">Standings</h2>
					<div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
						<table className="w-full">
							<thead>
								<tr className="border-b border-[var(--color-border)] text-left text-xs text-[var(--color-muted)]">
									<th className="px-3 py-2 font-normal">Rank</th>
									<th className="px-3 py-2 font-normal">Member</th>
									<th className="px-3 py-2 font-normal text-right">
										Return
									</th>
									<th className="px-3 py-2 font-normal text-right">
										Value
									</th>
								</tr>
							</thead>
							<tbody>
								{standings.data?.entries.map((e) => {
									const isMe = e.userId === currentUserId;
									return (
										<tr
											key={e.userId}
											className={`border-b border-[var(--color-border)] text-sm last:border-b-0 ${
												isMe
													? "bg-[var(--color-accent-soft)]"
													: ""
											}`}
										>
											<td className="px-3 py-2">{e.rank}</td>
											<td className="px-3 py-2">
												{e.username
													? `@${e.username}`
													: e.userId.slice(0, 8)}
												{isMe && (
													<span className="ml-2 text-xs text-[var(--color-muted)]">
														(you)
													</span>
												)}
											</td>
											<td className="px-3 py-2 text-right font-mono">
												{formatPct(e.returnPct)}
											</td>
											<td className="px-3 py-2 text-right font-mono">
												{formatDollars(e.totalValue)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</section>
			)}
		</div>
	);
}

function formatPct(s: string): string {
	const n = parseFloat(s);
	if (!Number.isFinite(n)) return s;
	const sign = n >= 0 ? "+" : "";
	return `${sign}${n.toFixed(2)}%`;
}

function formatDollars(s: string): string {
	const n = parseFloat(s);
	if (!Number.isFinite(n)) return s;
	return `$${n.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}
